import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, Form, Spinner } from 'react-bootstrap';
import { QRCodeSVG } from 'qrcode.react';
import { useAuth } from '@/contexts/AuthContext';
import { forgetReason, readReason } from '@/services/session/tabs';
import type { EndReason } from '@/services/session/limits';
import { CopyButton } from '@/components/ui/CopyButton';
import ArcanumLogo from '@/assets/arc_logo_black.svg?react';
import './LoginForm.css';

/** Why the portal signed this tab out, shown once on the sign-in page (TASK-649). */
const SIGN_OUT_MESSAGES: Record<Exclude<EndReason, 'manual'>, string> = {
  idle: 'You were signed out because there was no input in the portal for 2 hours.',
  'max-age': 'Sessions end 12 hours after sign-in. Sign in again to continue.',
  expired: 'Your session ended. Sign in again to continue.',
};

/**
 * The sign-in screen. One card, four steps: sign in, set a first password, set up an
 * authenticator, enter an authenticator code. The auth context decides which step shows.
 */
export default function LoginForm() {
  const navigate = useNavigate();
  const {
    signIn,
    completePasswordChange,
    completeMfaSetup,
    submitMfaCode,
    loading,
    error,
    passwordChangeRequired,
    mfaSetupRequired,
    mfaCodeRequired,
    clearError,
  } = useAuth();

  // Read once; removed after the first render, so a later reload does not show it again.
  const [signOutReason] = useState(() => readReason());
  useEffect(() => {
    if (signOutReason) forgetReason();
  }, [signOutReason]);

  const [formData, setFormData] = useState({
    username: '',
    password: '',
    newPassword: '',
    confirmPassword: '',
    fullName: '',
    mfaCode: '',
    mfaSetupCode: '',
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (error) clearError();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (passwordChangeRequired) {
      // Handle password change submission
      if (!formData.newPassword || formData.newPassword !== formData.confirmPassword || !formData.fullName) {
        return;
      }

      try {
        await completePasswordChange(formData.newPassword, formData.fullName);
      } catch {
        // Error is handled by the AuthContext
      }
    } else if (mfaSetupRequired) {
      // Handle MFA setup verify submission
      if (!formData.mfaSetupCode) {
        return;
      }

      try {
        await completeMfaSetup(formData.mfaSetupCode);
      } catch {
        // Error handled by context
      }
    } else if (mfaCodeRequired) {
      // Handle MFA code submission
      if (!formData.mfaCode) {
        return;
      }

      try {
        await submitMfaCode(formData.mfaCode);
      } catch {
        // Error handled by context
      }
    } else {
      // Handle initial sign in
      if (!formData.username || !formData.password) {
        return;
      }

      try {
        await signIn(formData.username, formData.password);
      } catch {
        // Error is handled by the AuthContext
      }
    }
  };

  const step = passwordChangeRequired
    ? 'Password change required'
    : mfaSetupRequired
      ? 'Set up your authenticator'
      : mfaCodeRequired
        ? 'Multi-factor authentication'
        : 'Sign in to continue';

  /** The one button of each step: its label, or a spinner and what is happening. */
  const submitLabel = (idle: string, busy: string) =>
    loading ? (
      <>
        <Spinner animation="border" size="sm" className="me-2" />
        {busy}
      </>
    ) : (
      idle
    );

  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-card__head">
          <ArcanumLogo className="login-logo auth-logo" />
          <h1 className="auth-title">Customer Success Portal</h1>
          <p className="auth-step">{step}</p>
        </div>

        {signOutReason && signOutReason !== 'manual' && !error && (
          <Alert variant="info">{SIGN_OUT_MESSAGES[signOutReason]}</Alert>
        )}

        {error && (
          <Alert variant="danger" dismissible onClose={clearError}>
            {error}
          </Alert>
        )}

        <Form onSubmit={handleSubmit}>
          {passwordChangeRequired ? (
            // Password change form
            <>
              <Alert variant="info">
                <strong>Welcome, {passwordChangeRequired.username}!</strong>
                <br />
                You need to set a new password and provide your full name to continue.
              </Alert>

              <Form.Group className="mb-3" controlId="auth-full-name">
                <Form.Label>Full name</Form.Label>
                <Form.Control
                  type="text"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  autoComplete="name"
                  required
                  disabled={loading}
                />
              </Form.Group>

              <Form.Group className="mb-3" controlId="auth-new-password">
                <Form.Label>New password</Form.Label>
                <Form.Control
                  type="password"
                  name="newPassword"
                  value={formData.newPassword}
                  onChange={handleInputChange}
                  autoComplete="new-password"
                  required
                  disabled={loading}
                  minLength={8}
                />
                <Form.Text>Must be at least 8 characters with uppercase, lowercase, and numbers.</Form.Text>
              </Form.Group>

              <Form.Group className="mb-3" controlId="auth-confirm-password">
                <Form.Label>Confirm new password</Form.Label>
                <Form.Control
                  type="password"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  autoComplete="new-password"
                  required
                  disabled={loading}
                  isInvalid={formData.confirmPassword !== '' && formData.newPassword !== formData.confirmPassword}
                />
                <Form.Control.Feedback type="invalid">Passwords do not match.</Form.Control.Feedback>
              </Form.Group>

              <Button
                type="submit"
                variant="primary"
                className="auth-submit"
                disabled={
                  loading ||
                  !formData.fullName ||
                  !formData.newPassword ||
                  formData.newPassword !== formData.confirmPassword
                }
              >
                {submitLabel('Set new password', 'Updating password')}
              </Button>
            </>
          ) : mfaSetupRequired ? (
            // MFA setup form
            <>
              <Alert variant="info">
                <strong>MFA is required for your account.</strong>
                <br />
                Scan the QR code with your authenticator app, then enter the 6-digit code it shows.
              </Alert>

              <div className="auth-qr">
                <QRCodeSVG value={mfaSetupRequired.otpauthUrl} size={180} level="M" title="Authenticator QR code" />
              </div>

              <Form.Group className="mb-3" controlId="auth-secret">
                <Form.Label>Secret key</Form.Label>
                <div className="auth-secret">
                  <Form.Control type="text" value={mfaSetupRequired.secretCode} readOnly />
                  <CopyButton value={mfaSetupRequired.secretCode} label="Copy secret key" size={15} />
                </div>
                <Form.Text>
                  Cannot scan the code? Type this key into Google Authenticator, 1Password, or Authy. Or use this link:{' '}
                  <a href={mfaSetupRequired.otpauthUrl}>otpauth://</a>
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-3" controlId="auth-mfa-setup-code">
                <Form.Label>Authentication code</Form.Label>
                <Form.Control
                  type="text"
                  name="mfaSetupCode"
                  value={formData.mfaSetupCode}
                  onChange={handleInputChange}
                  placeholder="6-digit code"
                  pattern="[0-9]*"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  required
                  disabled={loading}
                  autoFocus
                />
              </Form.Group>

              <Button
                type="submit"
                variant="primary"
                className="auth-submit"
                disabled={loading || !formData.mfaSetupCode}
              >
                {submitLabel('Verify and continue', 'Verifying')}
              </Button>
            </>
          ) : mfaCodeRequired ? (
            // MFA code prompt
            <>
              <Form.Group className="mb-3" controlId="auth-mfa-code">
                <Form.Label>Authentication code</Form.Label>
                <Form.Control
                  type="text"
                  name="mfaCode"
                  value={formData.mfaCode}
                  onChange={handleInputChange}
                  placeholder="6-digit code"
                  pattern="[0-9]*"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  required
                  disabled={loading}
                  autoFocus
                />
              </Form.Group>

              <Button type="submit" variant="primary" className="auth-submit" disabled={loading || !formData.mfaCode}>
                {submitLabel('Verify and sign in', 'Verifying')}
              </Button>
            </>
          ) : (
            // Regular login form
            <>
              <Form.Group className="mb-3" controlId="auth-email">
                <Form.Label>Email address</Form.Label>
                <Form.Control
                  type="email"
                  name="username"
                  value={formData.username}
                  onChange={handleInputChange}
                  autoComplete="username"
                  required
                  disabled={loading}
                />
              </Form.Group>

              <Form.Group className="mb-3" controlId="auth-password">
                <Form.Label>Password</Form.Label>
                <Form.Control
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  autoComplete="current-password"
                  required
                  disabled={loading}
                />
              </Form.Group>

              <Button
                type="submit"
                variant="primary"
                className="auth-submit"
                disabled={loading || !formData.username || !formData.password}
              >
                {submitLabel('Sign in', 'Signing in')}
              </Button>

              <Button
                variant="link"
                className="auth-link"
                onClick={() => navigate('/create-password?mode=reset')}
                disabled={loading}
              >
                Forgot your password?
              </Button>
            </>
          )}
        </Form>

        <p className="auth-foot">Arcanum AI</p>
      </div>
    </div>
  );
}
